import { useTranslation } from 'react-i18next';
import LongText from '__components/Inputs/LongText';
import { type FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { canSetAdvancedRecurrenceRule } from 'store/authentication/selectors';
import ButtonGroup from '__components/Inputs/ButtonGroup';
import { ButtonGroupImageButton } from '__components/Inputs/ButtonGroup/Button';
import InputControl from '__components/Inputs/InputControl';
import { KizenTypography } from '__app/typography';
import { BasicRRule } from './components/BasicRrule';
import { getModeOptions } from './helpers';
import { getRRuleFromString, isRRuleBasic } from '@kizen/rrule';
import { useFlashTransition } from '__hooks/useFlashState';
import ConfirmationModal from '__components/Modals/ConfirmationModal';
import { Occurrences } from './components/Ocurrences';
import { usePreserveCaretPosition } from 'hooks/usePreserveCaretPosition';

export type RRuleSchedulerState = {
  rrule: string;
  occurrences: Date[];
  isAdvanced: boolean;
};

type RRuleSchedulerProps = RRuleSchedulerState & {
  onChange: (state: RRuleSchedulerState) => void;
  error: string | null;
  setError: (error: string | null) => void;
};

export const RRuleScheduler: FC<RRuleSchedulerProps> = ({
  rrule,
  occurrences,
  isAdvanced,
  onChange,
  error,
  setError,
}) => {
  const { t } = useTranslation();
  const showAdvancedRecurrenceRule = useSelector(canSetAdvancedRecurrenceRule);

  const [showConfirmation, setShowConfirmation] = useState(false);

  const [message, showMessage, flashErrorMessage, cancelFlash] =
    useFlashTransition();

  const handleChangeMode = ({ value }: { value: 'basic' | 'advanced' }) => {
    if ((value === 'basic' && isAdvanced && !isRRuleBasic(rrule)) || error) {
      setShowConfirmation(true);
    } else {
      onChange({ rrule, occurrences, isAdvanced: value === 'advanced' });
      setError(null);
    }
  };

  const onResetConfirmed = () => {
    onChange({ rrule: '', occurrences: [], isAdvanced: false });
    setShowConfirmation(false);
    setError(null);
  };

  const onHideConfirmation = () => {
    setShowConfirmation(false);
  };

  const handleChangeBasicRule = ({
    rrule,
    occurrences,
  }: {
    rrule: string;
    occurrences: Date[];
  }) => {
    setError(null);
    onChange({ rrule, occurrences, isAdvanced });
  };

  const handleChangeRule = (rrule: string) => {
    setError(null);
    onChange({
      rrule: rrule.toUpperCase(),
      occurrences,
      isAdvanced,
    });
  };

  const handleBlurRule = () => {
    const { rule, occurrences } = getRRuleFromString(rrule);
    if (rrule) {
      if (!rule) {
        setError(t('Please enter a valid Recurrence Rule'));
      } else if (occurrences.length === 0) {
        setError(t('Recurrence Rule does not generate any occurrences'));
      }
    }
    onChange({
      occurrences,
      rrule,
      isAdvanced,
    });
  };

  useEffect(() => {
    if (error) {
      flashErrorMessage(error);
    } else {
      cancelFlash();
    }
  }, [isAdvanced, error, flashErrorMessage, cancelFlash]);

  const inputRef = usePreserveCaretPosition(rrule, isAdvanced);

  return (
    <>
      {!!showAdvancedRecurrenceRule && (
        <InputControl variant="outline" data-qa="rrule-mode" margin>
          <ButtonGroup
            value={isAdvanced ? 'advanced' : 'basic'}
            onChange={handleChangeMode}
            options={getModeOptions(t)}
            columns={3}
            mode="wizard"
            button={<ButtonGroupImageButton />}
            gutter={'20px'}
          />
        </InputControl>
      )}
      {isAdvanced ? (
        <LongText
          ref={inputRef}
          label={
            <>
              {t('RRule')} (
              <KizenTypography
                as="span"
                size="micro"
                type="anchor"
                target="_blank"
                href="https://developer.kizen.com/automation-rrules/"
              >
                {t('documentation')}
              </KizenTypography>
              )
            </>
          }
          data-qa="rrule-input"
          placeholder={t('Enter Recurrence Rule')}
          value={rrule}
          onChange={handleChangeRule}
          onBlur={handleBlurRule}
          error={!!error}
          validate={{ showMessage, message }}
          margin
          enableResize={false}
        />
      ) : (
        <BasicRRule
          rruleString={rrule}
          onChange={handleChangeBasicRule}
          error={error}
          showMessage={showMessage}
        />
      )}
      <Occurrences occurrences={occurrences} />
      {showConfirmation ? (
        <ConfirmationModal
          show
          onConfirm={onResetConfirmed}
          onHide={onHideConfirmation}
          buttonText={t('Discard Rule')}
          actionBtnColor="red"
        >
          {t(
            'The current rule is unsupported by the "Basic" editor and will be lost, would you like to continue?'
          )}
        </ConfirmationModal>
      ) : null}
    </>
  );
};
